import React, { useState } from 'react'
import {
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  Radio,
  Popconfirm
} from 'antd'
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'

const { Option } = Select

const PREVIEW_SEGMENT = gql`
  query previewNotificationSegment($segment: SendSegmentInput!) {
    previewNotificationSegment(segment: $segment)
  }
`

const ACTIVE_DISPATCH = gql`
  query activeNotificationDispatch($notificationId: ID!) {
    activeNotificationDispatch(notificationId: $notificationId) {
      id
      targetCount
      created_at
    }
  }
`

const SEND_NOTIFICATION = gql`
  mutation sendNotification($notificationId: ID!, $segment: SendSegmentInput!) {
    sendNotification(notificationId: $notificationId, segment: $segment) {
      id
      targetCount
      status
    }
  }
`

const buildSegment = values => {
  const { mode, language, version, comparison, lastActiveDays } = values
  const segment = { mode }
  if (language && language !== 'all') segment.language = language
  if (mode === 'appVersion') {
    segment.version = version
    segment.comparison = comparison || 'equal'
  }
  if (mode === 'lastActive') {
    segment.lastActiveDays = lastActiveDays
    segment.comparison = comparison === 'lower' ? 'lower' : 'greater'
  }
  return segment
}

const SendNotificationForm = ({ notificationId, onSent }) => {
  const [form] = Form.useForm()
  const [mode, setMode] = useState('broadcast')
  const [previewCount, setPreviewCount] = useState(null)
  const [sending, setSending] = useState(false)

  const [previewSegment, { loading: previewLoading }] = useLazyQuery(
    PREVIEW_SEGMENT,
    {
      fetchPolicy: 'network-only',
      onCompleted: data => setPreviewCount(data.previewNotificationSegment),
      onError: error => toast.error(`Ошибка предпросмотра: ${error.message}`)
    }
  )

  const { data: activeData, refetch: refetchActive } = useQuery(
    ACTIVE_DISPATCH,
    {
      variables: { notificationId },
      skip: !notificationId,
      fetchPolicy: 'network-only',
      pollInterval: 5000
    }
  )
  const activeDispatch = activeData?.activeNotificationDispatch

  const [sendNotification] = useMutation(SEND_NOTIFICATION, {
    onCompleted: data => {
      const { targetCount } = data.sendNotification
      setSending(false)
      toast.success(
        `Рассылка запущена: ${targetCount} получателей. Статус — во вкладке «История отправок»`
      )
      if (onSent) onSent()
    },
    onError: error => {
      setSending(false)
      toast.error(`Ошибка отправки: ${error.message}`)
      refetchActive()
    }
  })

  const handlePreview = async () => {
    const values = await form.validateFields()
    previewSegment({ variables: { segment: buildSegment(values) } })
  }

  const handleSend = async () => {
    if (sending || activeDispatch) return
    const values = await form.validateFields()
    setSending(true)
    sendNotification({
      variables: { notificationId, segment: buildSegment(values) }
    })
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={() => setPreviewCount(null)}
    >
      <Form.Item name="mode" label="Кому" initialValue="broadcast">
        <Radio.Group
          onChange={e => {
            setMode(e.target.value)
          }}
        >
          <Radio value="broadcast">Всем</Radio>
          <Radio value="appVersion">По версии приложения</Radio>
          <Radio value="lastActive">По последней активности</Radio>
        </Radio.Group>
      </Form.Item>

      {mode === 'appVersion' && (
        <>
          <Form.Item
            name="version"
            label="Версия (прим. 2.7.0)"
            rules={[{ required: true, message: 'Укажите версию' }]}
          >
            <Input placeholder="2.7.0" />
          </Form.Item>
          <Form.Item name="comparison" label="Сравнение" initialValue="equal">
            <Radio.Group>
              <Radio value="equal">Равно</Radio>
              <Radio value="greater">Новее</Radio>
              <Radio value="lower">Старее</Radio>
            </Radio.Group>
          </Form.Item>
        </>
      )}

      {mode === 'lastActive' && (
        <>
          <Form.Item
            name="lastActiveDays"
            label="Дней"
            rules={[{ required: true, message: 'Укажите число дней' }]}
          >
            <InputNumber min={1} placeholder="7" />
          </Form.Item>
          <Form.Item name="comparison" label="Условие" initialValue="greater">
            <Radio.Group>
              <Radio value="greater">Были активны за последние N дней</Radio>
              <Radio value="lower">Неактивны дольше N дней</Radio>
            </Radio.Group>
          </Form.Item>
        </>
      )}

      <Form.Item name="language" label="Язык приложения" initialValue="all">
        <Select style={{ width: 160 }}>
          <Option value="all">Все</Option>
          <Option value="kz">KZ</Option>
          <Option value="ru">RU</Option>
          <Option value="en">EN</Option>
        </Select>
      </Form.Item>

      <Button onClick={handlePreview} loading={previewLoading}>
        Показать количество получателей
      </Button>
      {previewCount !== null && (
        <p style={{ marginTop: 8 }}>Получателей: {previewCount}</p>
      )}

      <div style={{ marginTop: 16 }}>
        {activeDispatch && (
          <p>
            Рассылка по этому шаблону уже идёт ({activeDispatch.targetCount}{' '}
            получателей). Дождитесь её завершения — статус во вкладке «История
            отправок».
          </p>
        )}
        <Popconfirm
          title={`Отправить пуш${
            previewCount !== null ? ` (${previewCount} получателей)` : ''
          }?`}
          okText="Отправить"
          cancelText="Отмена"
          disabled={sending || !!activeDispatch}
          onConfirm={handleSend}
        >
          <Button
            type="primary"
            loading={sending}
            disabled={sending || !!activeDispatch}
          >
            Отправить
          </Button>
        </Popconfirm>
      </div>
    </Form>
  )
}

export default SendNotificationForm
